import { type ReactNode, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, FormProvider, useForm } from "react-hook-form";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { WFPButton } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCountryObjectFromCache } from "../../../../utils";
import { formatLanguageLabel, languages, onChangeLanguage } from "../../../../utils/i18n";
import type { Trader } from "../../types";

const MAX_PHONE_LENGTH = 12;

// "INSTITUTIONAL_BUYER" -> "Institutional buyer"
const formatTraderType = (value?: string) =>
  value
    ? value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/^./, c => c.toUpperCase())
    : value;

const createProfileSchema = (requiredMessage: string, phoneTooLongMessage: string) =>
  z.object({
    name: z
      .string()
      .optional()
      .refine(value => !!value && value.trim() !== "", { message: requiredMessage }),
    physical_address: z
      .string()
      .optional()
      .refine(value => !!value && value.trim() !== "", { message: requiredMessage }),
    first_name: z
      .string()
      .optional()
      .refine(value => !!value && value.trim() !== "", { message: requiredMessage }),
    last_name: z
      .string()
      .optional()
      .refine(value => !!value && value.trim() !== "", { message: requiredMessage }),
    contact_phone: z
      .string()
      .optional()
      .refine(value => !value || value.length <= MAX_PHONE_LENGTH, {
        message: phoneTooLongMessage,
      }),
    language: z
      .string()
      .optional()
      .refine(value => !!value && value.trim() !== "", { message: requiredMessage }),
  });

export type ProfileFormValues = z.infer<ReturnType<typeof createProfileSchema>>;

interface LanguageOption {
  value: string;
  label: string;
}

interface ProfileFormProps {
  trader: Trader;
  onValidate: (values: ProfileFormValues) => void | Promise<unknown>;
  initialValues?: Partial<ProfileFormValues>;
}

interface FieldShellProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
}

const FieldShell = ({ id, label, children, errorMessage, required = false }: FieldShellProps) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
      )}
    </Label>
    {children}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

export const ProfileForm = ({ trader, onValidate, initialValues = {} }: ProfileFormProps) => {
  const { t } = useTranslation();

  const buyerType = formatTraderType(trader.trader_type);

  // getCountryObjectFromCache lives in an untyped JS util; narrow to what we read.
  const country: { phone_prefix?: string; flag?: string } = getCountryObjectFromCache();
  const phonePrefix = country.phone_prefix ? `+${country.phone_prefix}` : "";

  const languageOptions = useMemo<LanguageOption[]>(
    () =>
      languages.map((language: { value: string; engLabel?: string; label: string }) => ({
        value: language.value,
        label: formatLanguageLabel(language),
      })),
    []
  );

  const schema = useMemo(
    () =>
      createProfileSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("profile.errors.mobileNumberCannotExceed12Digits")
      ),
    [t]
  );

  const methods = useForm<ProfileFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  // Preserve legacy behavior: the app UI language switches as soon as the user picks one.
  const language = watch("language");
  useEffect(() => {
    if (language) {
      onChangeLanguage({ value: language });
    }
  }, [language]);

  const getError = (name: FieldPath<ProfileFormValues>) =>
    (errors[name] as { message?: string } | undefined)?.message;

  const renderTextField = (name: FieldPath<ProfileFormValues>, label: string, required = false) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const error = getError(name);
        return (
          <FieldShell id={name} label={label} errorMessage={error} required={required}>
            <Input
              {...field}
              id={name}
              value={field.value ?? ""}
              aria-invalid={!!error}
              aria-describedby={error ? `${name}-error` : undefined}
            />
          </FieldShell>
        );
      }}
    />
  );

  const onSubmit = handleSubmit(async values => {
    await onValidate(values);
  });

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t("profile.form.header")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <section className="space-y-4">
              <h2 className="font-semibold text-base">
                {t("profile.form.traderDetails", { buyerType })}
              </h2>
              {renderTextField("name", t("profile.form.traderName", { buyerType }), true)}
              {renderTextField(
                "physical_address",
                t("profile.form.physicalAddress", { buyerType }),
                true
              )}
              <div className="space-y-2">
                <Label className="font-semibold text-foreground">
                  {t("profile.form.administrativeArea")}
                </Label>
                <p className="text-muted-foreground text-sm">
                  {trader.location_details?.name ?? "—"}
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="font-semibold text-base">
                {t("profile.form.traderRepresentativeDetails", { buyerType })}
              </h2>
              {renderTextField("first_name", t("profile.form.name"), true)}
              {renderTextField("last_name", t("profile.form.surname"), true)}

              <Controller
                control={control}
                name="contact_phone"
                render={({ field }) => {
                  const error = getError("contact_phone");
                  return (
                    <FieldShell
                      id="contact_phone"
                      label={t("profile.form.mobileNumber")}
                      errorMessage={error}
                    >
                      <div className="flex items-center gap-2">
                        {country.flag && <span aria-hidden="true">{country.flag}</span>}
                        {phonePrefix && (
                          <span className="text-muted-foreground text-sm">{phonePrefix}</span>
                        )}
                        <Input
                          {...field}
                          id="contact_phone"
                          type="tel"
                          value={field.value ?? ""}
                          className="flex-1"
                          aria-invalid={!!error}
                          aria-describedby={error ? "contact_phone-error" : undefined}
                        />
                      </div>
                    </FieldShell>
                  );
                }}
              />

              <Controller
                control={control}
                name="language"
                render={({ field }) => {
                  const error = getError("language");
                  return (
                    <FieldShell
                      id="language"
                      label={t("profile.form.preferredLanguage")}
                      errorMessage={error}
                      required
                    >
                      <Select value={field.value ?? ""} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="language"
                          className="w-full"
                          aria-invalid={!!error}
                          aria-describedby={error ? "language-error" : undefined}
                        >
                          <SelectValue placeholder={t("profile.form.preferredLanguage")} />
                        </SelectTrigger>
                        <SelectContent>
                          {languageOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FieldShell>
                  );
                }}
              />
            </section>
          </CardContent>
          <CardFooter className="justify-end">
            <WFPButton type="submit" disabled={isSubmitting}>
              <Check className="size-4" aria-hidden="true" />
              {t("profile.form.updateProfile")}
            </WFPButton>
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  );
};

// ponytail: 3rd local copy of FieldShell in the repo (Managers, AdminAreaTypes).
// Extract a shared form field wrapper only when a 4th appears or the others get migrated.
